import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AppHeader } from '@/components/layout/AppHeader';
import { setStudentToken } from '../authCookie';

interface StudentData {
  student_id: string;
  student_email: string;
}

interface StudentAuthProps {
  backendUrl?: string;
  onAuthenticated: (student: StudentData) => void;
}

const StudentAuth: React.FC<StudentAuthProps> = ({
  backendUrl = 'http://localhost:8000',
  onAuthenticated,
}) => {
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();

    if (!trimmed) {
      setError('Please enter your Yale email address.');
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/auth/student/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data?.detail || 'Request failed. Please try again.');
        return;
      }

      setEmailSentTo(data.email_sent_to);
      setStatus('Verification code sent! Check your email.');
      setStep('verify');
    } catch (err) {
      console.error('Error during student login start:', err);
      setError('Unable to reach the server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      setError('Please enter the verification code.');
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/auth/student/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: trimmedCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data?.detail || 'Verification failed. Please try again.');
        return;
      }

      if (data.session_token) {
        setStudentToken(data.session_token);
      }
      setStatus('Login successful!');
      onAuthenticated({ student_id: data.student_id, student_email: data.student_email });
    } catch (err) {
      console.error('Error during student verification:', err);
      setError('Unable to reach the server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setCode('');
    setError(null);
    setStatus(null);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader
        right={
          <Button variant="ghost" onClick={() => navigate('/professor')}>
            Professor sign in
          </Button>
        }
      />
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-soft sm:p-10">
          <h1 className="text-2xl font-bold tracking-tight">Verify your email</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {step === 'email'
              ? 'Enter your Yale email and we\'ll send you a one-time code. Your questions stay anonymous.'
              : `Enter the code we sent to ${emailSentTo}`}
          </p>

          <div className="mt-6 space-y-3 empty:hidden">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {status && (
              <Alert variant="success">
                <AlertDescription>{status}</AlertDescription>
              </Alert>
            )}
          </div>

          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="student-email">Yale email</Label>
                <Input
                  id="student-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane.doe@yale.edu"
                  autoComplete="email"
                  className="h-12 rounded-lg text-base md:text-base"
                />
              </div>
              <Button type="submit" variant="brand" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Sending code...' : 'Continue'}
                {!isSubmitting && <ArrowRight aria-hidden />}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifySubmit} className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="student-code">Verification code</Label>
                <Input
                  id="student-code"
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="h-14 rounded-lg text-center text-2xl font-semibold tracking-[0.35em] placeholder:tracking-[0.35em] placeholder:text-muted-foreground/50 md:text-2xl"
                />
                <p className="text-xs text-muted-foreground">
                  Didn&apos;t get it within 2 minutes? Check your Spam or Junk folder.
                </p>
              </div>
              <Button type="submit" variant="brand" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Verifying...' : 'Verify and sign in'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={handleBack}>
                Use a different email
              </Button>
            </form>
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">Only @yale.edu addresses are accepted.</p>
        </div>
      </main>
    </div>
  );
};

export default StudentAuth;
